import { useEffect, useRef, useState } from 'react';
import { getCustomer } from 'helpers/customer';
import { readSitePreview } from 'helpers/sitePreview';

const TOKEN_KEY = 'rayan_site_chat_token';
const NAME_KEY = 'rayan_site_chat_name';
const PHONE_KEY = 'rayan_site_chat_phone';
const ACTIVE_KEY = 'rayan_site_chat_on';
const CITY_KEY = 'rayan_site_chat_city';

const currentCity = () =>
	Number(getCustomer().store_id) === 1 ? 'Москва' : 'Нальчик';

const API =
	process.env.REACT_APP_CRM_API || 'https://crm.rayanhalal.ru/rest';

const makeToken = () => {
	if (window.crypto?.randomUUID) {
		return window.crypto.randomUUID().replace(/-/g, '');
	}
	return Array.from({ length: 32 }, () =>
		Math.floor(Math.random() * 16).toString(16),
	).join('');
};

const readToken = () => {
	let token = localStorage.getItem(TOKEN_KEY);
	if (!token || !/^[a-f0-9]{32}$/.test(token)) {
		token = makeToken();
		localStorage.setItem(TOKEN_KEY, token);
	}
	return token;
};

const SiteChatWidget = () => {
	const [allowed] = useState(readSitePreview);
	const [open, setOpen] = useState(false);
	const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || '');
	const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_KEY) || '');
	const [draft, setDraft] = useState('');
	const [messages, setMessages] = useState([]);
	const [unread, setUnread] = useState(0);
	const [closed, setClosed] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	const [started, setStarted] = useState(
		() => localStorage.getItem(ACTIVE_KEY) === '1',
	);
	const listRef = useRef(null);
	const afterRef = useRef(0);
	const openRef = useRef(false);

	const scrollDown = () => {
		const el = listRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	};

	const applyMessages = (incoming, replace) => {
		if (!Array.isArray(incoming) || !incoming.length) return;
		setMessages(prev => {
			const base = replace ? [] : prev;
			const known = new Set(base.map(item => item.id));
			const next = incoming.filter(item => !known.has(item.id));
			const merged = [...base, ...next];
			const last = merged[merged.length - 1];
			if (last) afterRef.current = last.id;
			return merged;
		});
		requestAnimationFrame(scrollDown);
	};

	const poll = async mark => {
		if (localStorage.getItem(ACTIVE_KEY) !== '1') return;
		const token = readToken();
		const params = new URLSearchParams({
			token,
			after: String(mark ? afterRef.current : 0),
			mark: mark ? '1' : '0',
			store_id: String(currentCity() === 'Москва' ? 1 : 0),
		});
		const res = await fetch(`${API}/crm-site-chat/poll?${params}`);
		if (!res.ok) return;
		const data = await res.json();
		if (mark && afterRef.current === 0) {
			applyMessages(data.messages, true);
		} else if (mark) {
			applyMessages(data.messages, false);
		}
		setUnread(mark ? 0 : Number(data.unread) || 0);
		setClosed(!!data.closed);
	};

	useEffect(() => {
		openRef.current = open;
	}, [open]);

	useEffect(() => {
		if (!allowed || !started) return undefined;
		let stop = false;
		const tick = () => {
			if (stop || document.hidden) return;
			poll(openRef.current).catch(() => {});
		};
		tick();
		const timer = setInterval(tick, open ? 4000 : 25000);
		return () => {
			stop = true;
			clearInterval(timer);
		};
	}, [allowed, open, started]);

	if (!allowed) return null;

	const cityName = currentCity();

	const send = async () => {
		const text = draft.trim();
		if (!text || sending) return;
		const city = currentCity();
		setSending(true);
		setError('');
		const token = readToken();
		localStorage.setItem(NAME_KEY, name.trim());
		localStorage.setItem(PHONE_KEY, phone.trim());
		try {
			const res = await fetch(`${API}/crm-site-chat/send`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					token,
					name: name.trim(),
					phone: phone.trim(),
					text,
					store_id: city === 'Москва' ? 1 : 0,
				}),
			});
			const data = await res.json().catch(() => ({}));
			if (!res.ok) {
				const serverError =
					typeof data.error === 'string' && data.error !== 'Page not found'
						? data.error
						: '';
				setError(serverError || 'Не отправилось. Попробуйте ещё раз');
				return;
			}
			localStorage.setItem(CITY_KEY, city);
			localStorage.setItem(ACTIVE_KEY, '1');
			setStarted(true);
			setDraft('');
			if (data.id) {
				applyMessages([data], false);
			}
			setClosed(false);
		} catch (e) {
			setError('Нет связи. Проверьте интернет и отправьте ещё раз');
		} finally {
			setSending(false);
		}
	};

	return (
		<div className='site-chat'>
			{open && (
				<section className='site-chat-panel' aria-label='Чат с поддержкой'>
					<header className='site-chat-head'>
						<div>
							<strong>Поддержка</strong>
							<p>Ответим в этом чате</p>
						</div>
						<button type='button' className='site-chat-x' onClick={() => setOpen(false)} aria-label='Закрыть чат'>
							×
						</button>
					</header>
					<div className='site-chat-log' ref={listRef}>
						<div className='site-chat-city'>Город: {cityName}</div>
						<div className='site-chat-bubble site-chat-bubble--them'>
							Здравствуйте. Напишите вопрос по заказу или доставке — ответит сотрудник.
						</div>
						{messages.map(msg => (
							<div
								key={msg.id}
								className={`site-chat-bubble ${
									msg.side === 'visitor' ? 'site-chat-bubble--me' : 'site-chat-bubble--them'
								}`}
							>
								{msg.body}
							</div>
						))}
						{closed && (
							<div className='site-chat-note'>
								Диалог завершён. Напишите снова, если вопрос ещё актуален.
							</div>
						)}
					</div>
					{!started && (
						<div className='site-chat-fields'>
							<input
								value={name}
								placeholder='Как к вам обращаться'
								maxLength={40}
								onChange={e => setName(e.target.value)}
							/>
							<input
								value={phone}
								placeholder='Телефон, если удобно'
								maxLength={20}
								onChange={e => setPhone(e.target.value)}
							/>
						</div>
					)}
					{error && <div className='site-chat-error'>{error}</div>}
					<form
						className='site-chat-form'
						onSubmit={e => {
							e.preventDefault();
							send();
						}}
					>
						<textarea
							rows={2}
							maxLength={1000}
							placeholder='Сообщение'
							value={draft}
							onChange={e => setDraft(e.target.value)}
							onKeyDown={e => {
								if (e.key === 'Enter' && !e.shiftKey) {
									e.preventDefault();
									send();
								}
							}}
						/>
						<button type='submit' disabled={sending || !draft.trim()}>
							{sending ? '...' : 'Отправить'}
						</button>
					</form>
				</section>
			)}
			{!open && (
			<button
				type='button'
				className='site-chat-fab'
				aria-label={open ? 'Закрыть чат' : 'Открыть чат'}
				onClick={() => setOpen(value => !value)}
			>
				<svg width='26' height='26' viewBox='0 0 24 24' aria-hidden='true'>
					<path
						fill='currentColor'
						d='M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'
					/>
				</svg>
				{unread > 0 && <span className='site-chat-badge'>{unread > 9 ? '9+' : unread}</span>}
			</button>
			)}
		</div>
	);
};

export default SiteChatWidget;
